/**
 * KERN Feedback Widget - Image Format Validation
 *
 * Security utilities for validating image file formats by checking
 * magic bytes (file signatures) to prevent malicious file uploads.
 */

/**
 * Supported image formats for upload
 */
export type AllowedImageFormat = 'png' | 'jpeg' | 'webp' | 'gif';

/**
 * Result of image validation
 */
export interface ImageValidationResult {
  valid: boolean;
  detectedFormat: AllowedImageFormat | 'svg' | 'unknown' | null;
  error?: string;
}

/**
 * Magic byte signatures for supported image formats
 */
const IMAGE_SIGNATURES: Record<
  string,
  { signature: number[]; offset?: number; format: AllowedImageFormat | 'svg' }
> = {
  // PNG: 89 50 4E 47 0D 0A 1A 0A
  png: {
    signature: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    format: 'png',
  },
  // JPEG: FF D8 FF
  jpeg: {
    signature: [0xff, 0xd8, 0xff],
    format: 'jpeg',
  },
  // GIF87a: 47 49 46 38 37 61
  gif87a: {
    signature: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
    format: 'gif',
  },
  // GIF89a: 47 49 46 38 39 61
  gif89a: {
    signature: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
    format: 'gif',
  },
  // WebP: RIFF....WEBP
  webp_riff: {
    signature: [0x52, 0x49, 0x46, 0x46],
    format: 'webp',
  },
};

/**
 * SVG detection patterns (to reject SVG files even if disguised)
 */
const SVG_PATTERNS = [
  '<svg',
  '<?xml',
  '<!DOCTYPE svg',
  'xmlns="http://www.w3.org/2000/svg"',
  "xmlns='http://www.w3.org/2000/svg'",
];

/**
 * Read file as ArrayBuffer
 */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file as ArrayBuffer'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Check if bytes match a signature at a given offset
 */
function matchesSignature(bytes: Uint8Array, signature: number[], offset: number = 0): boolean {
  if (bytes.length < offset + signature.length) {
    return false;
  }
  for (let i = 0; i < signature.length; i++) {
    if (bytes[offset + i] !== signature[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Check if file contains SVG content (text-based detection)
 * This catches SVG files even if they have wrong extensions or MIME types
 */
function containsSvgContent(bytes: Uint8Array): boolean {
  // Check first 1KB for SVG patterns (SVG files are text-based)
  const checkLength = Math.min(bytes.length, 1024);
  let text = '';

  try {
    // Decode as UTF-8 text
    const decoder = new TextDecoder('utf-8', { fatal: false });
    text = decoder.decode(bytes.slice(0, checkLength)).toLowerCase();
  } catch {
    // If decoding fails, it's likely a binary file (which is good)
    return false;
  }

  // Check for SVG patterns
  for (const pattern of SVG_PATTERNS) {
    if (text.includes(pattern.toLowerCase())) {
      return true;
    }
  }

  return false;
}

/**
 * Detect the actual image format from file bytes
 */
function detectImageFormat(bytes: Uint8Array): AllowedImageFormat | 'svg' | 'unknown' {
  // First, check for SVG content (security critical - must be rejected)
  if (containsSvgContent(bytes)) {
    return 'svg';
  }

  // Check PNG signature
  if (matchesSignature(bytes, IMAGE_SIGNATURES.png.signature)) {
    return 'png';
  }

  // Check JPEG signature
  if (matchesSignature(bytes, IMAGE_SIGNATURES.jpeg.signature)) {
    return 'jpeg';
  }

  // Check GIF signatures (both versions)
  if (
    matchesSignature(bytes, IMAGE_SIGNATURES.gif87a.signature) ||
    matchesSignature(bytes, IMAGE_SIGNATURES.gif89a.signature)
  ) {
    return 'gif';
  }

  // Check WebP signature (RIFF header + WEBP at offset 8)
  if (matchesSignature(bytes, IMAGE_SIGNATURES.webp_riff.signature)) {
    // WebP has "WEBP" at bytes 8-11
    const webpMarker = [0x57, 0x45, 0x42, 0x50]; // "WEBP"
    if (matchesSignature(bytes, webpMarker, 8)) {
      return 'webp';
    }
  }

  return 'unknown';
}

/**
 * Validate PNG file structure beyond magic bytes
 * Checks for valid IHDR chunk which must follow the signature
 */
function validatePngStructure(bytes: Uint8Array): boolean {
  // PNG signature is 8 bytes, followed by IHDR chunk
  // IHDR chunk: 4 bytes length + 4 bytes "IHDR" + 13 bytes data + 4 bytes CRC = 25 bytes minimum
  if (bytes.length < 8 + 25) {
    return false;
  }

  // Check IHDR chunk type at offset 12 (after 8-byte signature + 4-byte length)
  const ihdrMarker = [0x49, 0x48, 0x44, 0x52]; // "IHDR"
  if (!matchesSignature(bytes, ihdrMarker, 12)) {
    return false;
  }

  // Read IHDR length (should be 13 for valid PNG)
  const ihdrLength = (bytes[8] << 24) | (bytes[9] << 16) | (bytes[10] << 8) | bytes[11];
  if (ihdrLength !== 13) {
    return false;
  }

  return true;
}

/**
 * Validate JPEG file structure beyond magic bytes
 * Checks for valid JPEG markers
 */
function validateJpegStructure(bytes: Uint8Array): boolean {
  // JPEG must start with FF D8 and end with FF D9
  if (bytes.length < 4) {
    return false;
  }

  // Check SOI marker (Start Of Image)
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return false;
  }

  // Check for valid APP0/APP1 marker after SOI (JFIF or EXIF)
  // Should be FF E0 (JFIF) or FF E1 (EXIF) or other valid marker
  if (bytes[2] !== 0xff) {
    return false;
  }

  // Marker should be in valid range (E0-EF for APP markers, or other valid markers)
  const marker = bytes[3];
  const validMarkers = [
    0xe0,
    0xe1,
    0xe2,
    0xe3,
    0xe4,
    0xe5,
    0xe6,
    0xe7, // APP0-APP7
    0xe8,
    0xe9,
    0xea,
    0xeb,
    0xec,
    0xed,
    0xee,
    0xef, // APP8-APP15
    0xdb, // DQT (Define Quantization Table)
    0xc0,
    0xc1,
    0xc2,
    0xc3, // SOF markers
    0xc4, // DHT (Define Huffman Table)
    0xfe, // COM (Comment)
  ];

  if (!validMarkers.includes(marker)) {
    return false;
  }

  return true;
}

/**
 * Validate GIF file structure beyond magic bytes
 * Checks for valid GIF header including Logical Screen Descriptor
 */
function validateGifStructure(bytes: Uint8Array): boolean {
  // GIF minimum: 6 (header) + 7 (Logical Screen Descriptor) = 13 bytes
  if (bytes.length < 13) {
    return false;
  }

  // Already validated "GIF87a" or "GIF89a" in magic bytes
  // Check Logical Screen Descriptor (starts at byte 6)
  // Width and height should be > 0
  const width = bytes[6] | (bytes[7] << 8);
  const height = bytes[8] | (bytes[9] << 8);

  if (width === 0 || height === 0) {
    return false;
  }

  return true;
}

/**
 * Validate WebP file structure beyond magic bytes
 * Checks for valid RIFF/WebP structure
 */
function validateWebpStructure(bytes: Uint8Array): boolean {
  // WebP minimum: RIFF (4) + size (4) + WEBP (4) + chunk = 20+ bytes
  if (bytes.length < 20) {
    return false;
  }

  // Read RIFF chunk size (little-endian, bytes 4-7)
  const riffSize = bytes[4] | (bytes[5] << 8) | (bytes[6] << 16) | (bytes[7] << 24);

  // RIFF size should be file size - 8 (accounting for RIFF header)
  // Allow some tolerance for truncated files during preview
  if (riffSize < 12) {
    return false;
  }

  // Check for valid WebP chunk type at offset 12
  // VP8 (lossy), VP8L (lossless), or VP8X (extended)
  const vp8Marker = [0x56, 0x50, 0x38]; // "VP8"
  if (!matchesSignature(bytes, vp8Marker, 12)) {
    return false;
  }

  return true;
}

/**
 * Validate image format structure based on detected type
 */
function validateImageStructure(bytes: Uint8Array, format: AllowedImageFormat): boolean {
  switch (format) {
    case 'png':
      return validatePngStructure(bytes);
    case 'jpeg':
      return validateJpegStructure(bytes);
    case 'gif':
      return validateGifStructure(bytes);
    case 'webp':
      return validateWebpStructure(bytes);
    default:
      return false;
  }
}

/**
 * Main validation function - validates an image file for security
 *
 * @param file - The file to validate
 * @returns Promise resolving to validation result
 */
export async function validateImageFile(file: File): Promise<ImageValidationResult> {
  try {
    // Read file into buffer
    const arrayBuffer = await readFileAsArrayBuffer(file);
    const bytes = new Uint8Array(arrayBuffer);

    // Check minimum file size
    if (bytes.length < 8) {
      return {
        valid: false,
        detectedFormat: null,
        error: 'Die Datei ist zu klein und kein gültiges Bildformat.',
      };
    }

    // Detect actual format from magic bytes
    const detectedFormat = detectImageFormat(bytes);

    // Reject SVG files (security risk - can contain malicious scripts)
    if (detectedFormat === 'svg') {
      return {
        valid: false,
        detectedFormat: 'svg',
        error:
          'SVG-Dateien sind aus Sicherheitsgründen nicht erlaubt. Bitte verwende PNG, JPEG, WebP oder GIF.',
      };
    }

    // Reject unknown formats
    if (detectedFormat === 'unknown') {
      return {
        valid: false,
        detectedFormat: 'unknown',
        error:
          'Das Dateiformat konnte nicht erkannt werden. Erlaubte Formate: PNG, JPEG, WebP, GIF.',
      };
    }

    // Validate file structure for the detected format
    if (!validateImageStructure(bytes, detectedFormat)) {
      return {
        valid: false,
        detectedFormat,
        error: `Die Datei scheint beschädigt oder kein gültiges ${detectedFormat.toUpperCase()}-Bild zu sein.`,
      };
    }

    // Check if claimed MIME type matches detected format
    const mimeToFormat: Record<string, AllowedImageFormat> = {
      'image/png': 'png',
      'image/jpeg': 'jpeg',
      'image/jpg': 'jpeg',
      'image/webp': 'webp',
      'image/gif': 'gif',
    };

    const claimedFormat = mimeToFormat[file.type];
    if (claimedFormat && claimedFormat !== detectedFormat) {
      // File claims to be one format but is actually another
      // This could be an attempt to bypass validation
      console.warn(
        `KERN Feedback: File MIME type mismatch. Claimed: ${file.type}, Detected: ${detectedFormat}`
      );
      // We still accept it if it's a valid allowed format, but log the discrepancy
    }

    return {
      valid: true,
      detectedFormat,
    };
  } catch (error) {
    console.error('KERN Feedback: Image validation error:', error);
    return {
      valid: false,
      detectedFormat: null,
      error: 'Fehler beim Validieren der Datei. Bitte versuche es erneut.',
    };
  }
}

/**
 * Get allowed MIME types for mobile file upload (excludes SVG for security)
 */
export function getAllowedMimeTypes(): string[] {
  return ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
}

/**
 * Get accept attribute value for file input (excludes SVG for security)
 */
export function getFileInputAcceptAttribute(): string {
  return 'image/png,image/jpeg,image/webp,image/gif';
}

/**
 * Get human-readable list of allowed formats
 */
export function getAllowedFormatsText(): string {
  return 'PNG, JPEG, WebP, GIF';
}
